/**
 * Shared display helpers for the LMS staff-ops tabs (Phase 10A).
 *
 * Kept in one module so every tab formats DRF's Decimal-as-string amounts,
 * ISO datetimes and phone numbers identically, and so the DataTable styling
 * matches the existing pagesadmin convention (copied from RoleManagement.tsx).
 */
import type { TableStyles } from 'react-data-table-component';

/** DRF renders DecimalFields as strings — format defensively for both. */
export const formatAmount = (value: string | number | null | undefined): string => {
    if (value === null || value === undefined || value === '') return '-';
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);
    return num.toLocaleString('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 2,
    });
};

/**
 * ₹ with an Indian lakh/crore abbreviation — for chart axes and KPI headlines,
 * where the full `formatAmount()` output ("₹1,23,45,678.00") is too wide to read
 * at a glance and, on a y-axis, simply does not fit.
 *
 * Takes `string | number` because every money field on the disbursal metrics
 * endpoint is a DECIMAL STRING (see lmsStaffApi.DisbursedLoanMetrics): this is
 * the single place the `Number()` conversion is allowed to happen, at the moment
 * of display, so no arithmetic upstream ever runs on a lossy float.
 *
 * Thresholds are the Indian ones (1 crore = 1e7, 1 lakh = 1e5), not the
 * Western M/K, because the audience reads the register in lakhs.
 */
export const formatCompactAmount = (
    value: string | number | null | undefined
): string => {
    if (value === null || value === undefined || value === '') return '₹0';
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isNaN(num)) return String(value);

    const sign = num < 0 ? '-' : '';
    const abs = Math.abs(num);

    // `toLocaleString` with maximumFractionDigits: 2 keeps "₹1.25 Cr" readable
    // while never showing a misleading rounded-to-zero value for small sums.
    if (abs >= 1e7) return `${sign}₹${(abs / 1e7).toFixed(2)} Cr`;
    if (abs >= 1e5) return `${sign}₹${(abs / 1e5).toFixed(2)} L`;
    if (abs >= 1e3) return `${sign}₹${(abs / 1e3).toFixed(1)}K`;
    return `${sign}₹${abs.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

/**
 * "1–4 Aug" / "1 Aug – 4 Sep" for an inclusive IST `YYYY-MM-DD` range.
 *
 * The disbursal comparison is "same period last month, DAY-ALIGNED and clamped"
 * (plan decision A), so the compared windows MUST be spelled out on screen —
 * "+33%" against an unstated baseline is a number nobody can check.
 *
 * Parsed by splitting the string rather than with `new Date(value)`: the latter
 * treats a bare `YYYY-MM-DD` as UTC midnight and would render the previous day
 * for any user west of Greenwich.
 */
export const formatDayRange = (
    start: string | null | undefined,
    end: string | null | undefined
): string => {
    const parse = (value: string) => {
        const [y, m, d] = value.split('-').map(Number);
        if (!y || !m || !d) return null;
        return new Date(y, m - 1, d);
    };
    if (!start || !end) return '';
    const s = parse(start);
    const e = parse(end);
    if (!s || !e) return `${start} – ${end}`;

    const monthOf = (date: Date) =>
        date.toLocaleDateString('en-IN', { month: 'short' });

    // Same month is the overwhelmingly common case (1–4 Aug); only spell the
    // month twice when the range actually straddles one.
    if (s.getMonth() === e.getMonth() && s.getFullYear() === e.getFullYear()) {
        return `${s.getDate()}–${e.getDate()} ${monthOf(e)}`;
    }
    return `${s.getDate()} ${monthOf(s)} – ${e.getDate()} ${monthOf(e)}`;
};

export const formatDate = (value: string | null | undefined): string => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString('en-IN', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
    });
};

export const formatDateTime = (value: string | null | undefined): string => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
};

/**
 * Format a UTC datetime string as IST date + time (DD/MM/YYYY, HH:MM:SS in 24h).
 *
 * Use this — not `formatDate` — for `date_of_disbursal` and any other field
 * where the TIME component is meaningful (e.g. a Caprion disbursal at 14:46:28
 * IST must not be shown as "30 Sept 2026" with the time silently discarded).
 *
 * Output example: "29/09/2026, 14:46:28"
 */
export const formatDateTimeIST = (value: string | null | undefined): string => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleString('en-IN', {
        timeZone: 'Asia/Kolkata',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false,
    });
};

/**
 * `YYYY-MM-DD` for `<input type="date">` values and for the calendar endpoint's
 * path segment (GET /api/lms/staff_ops/calendar/<date_str>/, which parses with
 * `datetime.strptime(date_str, "%Y-%m-%d")` — views_staff_ops.py:2127).
 *
 * Uses the LOCAL calendar fields rather than `toISOString()` on purpose:
 * `toISOString()` converts to UTC first, which shifts the date by a day for
 * any user east of Greenwich (e.g. a date picked in IST/SGT late in the day).
 */
export const toDateInputValue = (date: Date): string => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

/**
 * The IST calendar date (`YYYY-MM-DD`) of an ISO datetime returned by the API.
 *
 * The backend builds every `settlement_datetime` in Asia/Kolkata
 * (views_staff_ops.py `_IST.localize(...)`), so comparing a presentation's date
 * against a staff-picked date must be done in IST — not in the browser's local
 * zone, which for an offshore ops user would otherwise be off by a day.
 * `en-CA` is used purely because its short date format is already YYYY-MM-DD.
 */
export const istDateString = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
};

/**
 * WhatsApp deep links must carry digits only. Mirrors the exact convention in
 * components/CallbackHelp.tsx (`https://wa.me/91${phoneNumber}`) — the 91
 * country code is prefixed by the caller, so any leading 91/0 already present
 * in the stored value is stripped here to avoid a doubled prefix.
 *
 * The backend returns `registered_phone` as a plain string cast of
 * IndividualDetails.phone_number (a BigIntegerField — views_staff_ops.py:287),
 * i.e. normally a bare 10-digit Indian mobile number.
 */
export const digitsOnly = (phone: string): string => {
    const stripped = (phone || '').replace(/\D/g, '');
    if (stripped.length > 10 && stripped.startsWith('91')) return stripped.slice(-10);
    if (stripped.length === 11 && stripped.startsWith('0')) return stripped.slice(1);
    return stripped;
};

/** react-data-table-component styling, matching pagesadmin/RoleManagement.tsx. */
export const dataTableStyles: TableStyles = {
    header: { style: { minHeight: '56px' } },
    headRow: {
        style: {
            backgroundColor: '#f9fafb',
            borderTopStyle: 'solid',
            borderTopWidth: '1px',
            borderTopColor: '#e5e7eb',
        },
    },
    headCells: {
        style: { fontWeight: '600', color: '#374151', fontSize: '0.875rem' },
    },
    cells: { style: { fontSize: '0.875rem', color: '#4b5563' } },
};
